<template>
  <div>
    <vx-card>
      <data-table
        :column-defs="columnDefs"
        :default-col-def="defaultColDef"
        :row-data="rowData"
        :total="total"
        :current-page="currentPage"
        :grid-options="gridOptions"
        :pagination-page-size="pageSize"
        pagination
        @update:current-page="onUpdateCurrentPage"
        @update:pagination-page-size="onUpdatePaginationPageSize"
      >
        <template #actions>
          <vs-button
            class="ml-4"
            @click="onClickAdd"
          >
            登録
          </vs-button>
        </template>
      </data-table>
    </vx-card>
    <form-modal
      v-model="showForm"
      :title="`施策${measure.id ? '更新' : '登録'}`"
      :submit-button-text="measure.id ? '更新' : '登録'"
      @click:submit="onClickSubmit"
    >
      <template #content>
        <ValidationForm
          ref="measureFormObserver"
        >
          <measure-form
            :id="measure.id"
            v-model:name="measure.name"
            v-model:category-id="measure.categoryId"
            v-model:asin="measure.asin"
            v-model:date="measure.date"
            v-model:before-date="measure.beforeDate"
            v-model:after-date="measure.afterDate"
            v-model:kpi-id="measure.kpiId"
            v-model:before-memo="measure.beforeMemo"
            v-model:after-memo="measure.afterMemo"
            :is-seller="true"
            :is-maker="false"
            :is-sourcing="false"
            class="mt-4 mb-12"
          />
        </ValidationForm>
      </template>
    </form-modal>
    <confirm-modal
      v-model="showConfirmModal"
      title="施策削除"
      @click:confirm="onClickConfirm"
      @click:cancel="showConfirmModal = false"
    >
      <template #content>
        <p class="p-4 whitespace-pre-wrap">
          {{ `${measure.name}` }} を本当に削除しますか？
        </p>
      </template>
    </confirm-modal>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import DataTable from '@/components/table/DataTable'
import DataTableCellActions from '@/components/table/DataTableCellActions'
import DataTableCellReport from '@/components/table/DataTableCellReport'
import FormModal from '@/components/modals/FormModal'
import ConfirmModal from '@/components/modals/ConfirmModal'
import MeasureForm from '@/components/forms/MeasureForm'
import measureStatus from '@/constants/measureStatus'
import { debounce } from '@/utils'

export default {
  name: 'Measure',
  components: {
    DataTable,
    FormModal,
    ConfirmModal,
    MeasureForm
  },
  data () {
    return {
      query: null,
      sortParam: '',
      currentPage: 1,
      total: 0,
      pageSize: 20,
      defaultColDef: {
        sortable: false,
        editable: false,
        resizable: false,
        suppressMenu: true
      },
      columnDefs: [
        {
          headerName: 'アクション',
          field: 'action',
          cellRenderer: 'dataTableCellActions',
          cellRendererParams : {
            editable: true,
            deletable: true,
            onClickEdit : this.onClickEdit,
            onClickTrash: this.onClickTrash
          }
        },
        {
          headerName: '登録日',
          field: 'createdAt'
        },
        {
          headerName: '施策名',
          field: 'name'
        },
        {
          headerName: 'カテゴリー',
          field: 'categoryId',
          valueFormatter: this.innerCategoryText
        },
        {
          headerName: '施策反映日',
          field: 'date'
        },
        {
          headerName: 'ステータス',
          field: 'status',
          valueFormatter: this.innerStatusText
        },
        {
          headerName: '売上成長率',
          field: 'salesImpact',
          valueFormatter (params) {
            return params.value ? `${params.value} ％` : null
          }
        },
        {
          headerName: 'KPI成長率',
          field: 'kpiImpact',
          valueFormatter (params) {
            return params.value ? `${params.value} ％` : null
          }
        },
        {
          headerName: 'レポート',
          field: 'reportUrl',
          cellRenderer: 'dataTableCellReport',
          cellRendererParams : {
            action : this.reportAction.bind(this)
          }
        }
      ],
      gridOptions: {},
      showForm: false,
      showConfirmModal: false,
      measure: {
        id: null,
        name: null,
        categoryId: null,
        asin: null,
        date: null,
        beforeDate: null,
        afterDate: null,
        kpiId: null,
        beforeMemo: null,
        afterMemo: null
      }
    }
  },
  computed: {
    ...mapState('measure', ['categories', 'measures']),
    rowData: {
      get () {
        return JSON.parse(JSON.stringify(this.measures))
      }
    }
  },
  beforeMount () {
    this.gridOptions = {
      autoSizeStrategy: {
        type: 'fitGridWidth',
        defaultMinWidth: 120,
        columnLimits: [{
          colId: 'action',
          maxWidth: 120
        },{
          colId: 'createdAt',
          maxWidth: 130
        },{
          colId: 'categoryId',
          maxWidth: 150
        },{
          colId: 'date',
          maxWidth: 130
        },{
          colId: 'status',
          maxWidth: 120
        },{
          colId: 'salesImpact',
          maxWidth: 120
        },{
          colId: 'kpiImpact',
          maxWidth: 120
        },{
          colId: 'reportUrl',
          maxWidth: 120
        }]
      },
      components: {
        dataTableCellActions: DataTableCellActions,
        dataTableCellReport: DataTableCellReport
      }
    }
  },
  async mounted () {
    await this.listCategories()
  },
  unmounted () {
    this.destroyMeasure()
  },
  methods: {
    ...mapActions('measure', [
      'listCategories',
      'listMeasures',
      'getMeasure',
      'addMeasure',
      'updateMeasure',
      'deleteMeasure',
      'destroyMeasure'
    ]),
    innerStatusText (params) {
      return measureStatus[params.value]
    },
    innerCategoryText (params) {
      return this.categories[params.value].name
    },
    fetchData: debounce(async (self) => {
      try {
        const payload = {
          page: self.currentPage,
          per_page: self.pageSize,
          sort: self.sortParam,
          q: self.query
        }
        const res = await self.listMeasures(payload)
        self.total = res.data.total
      } catch (e) {
        console.log(e)
      }
    }, 500),
    onUpdateCurrentPage (val) {
      this.currentPage = val
      this.fetchData(this)
    },
    onUpdatePaginationPageSize (val) {
      this.currentPage = 1
      this.pageSize = val
      this.fetchData(this)
    },
    async onClickEdit (e) {
      const res = await this.getMeasure({
        id: this.measures[e.index].id
      })
      const measure = res.data.data
      const asin = (measure.asin) ? measure.asin.replace(/,/g, '\n') : ''
      const beforeDate = (measure.beforeStartDate) ? [measure.beforeStartDate, measure.beforeEndDate] : ''
      const afterDate = (measure.afterStartDate) ? [measure.afterStartDate, measure.afterEndDate] : ''
      this.measure = {
        id: measure.id,
        name: measure.name,
        categoryId: `${measure.categoryId}`,
        asin,
        date: measure.date,
        beforeDate,
        afterDate,
        kpiId: `${measure.kpiId}`,
        beforeMemo: measure.beforeMemo,
        afterMemo: measure.afterMemo
      }
      this.showForm = true
    },
    async onClickAdd () {
      await this.initializeMeasure()
      this.showForm = true
    },
    async onClickTrash (e) {
      const measure = this.measures[e.index]
      this.measure.id = measure.id
      this.measure.name = measure.name
      this.showConfirmModal = true
    },
    async onClickConfirm () {
      await this.deleteMeasure({
        id: this.measure.id
      })
      this.showConfirmModal = false
      this.fetchData(this)
    },
    async onClickSubmit () {
      try {
        const result = await this.$refs.measureFormObserver.validate()
        if (!result.valid) return
        const data = { ...this.measure }
        if (data.id) {
          await this.updateMeasure(data)
        } else {
          await this.addMeasure(data)
        }
        this.showForm = false
        // ちょっと遅らせてリセットしてあげないと、モーダルが閉じるまでにフォームの値が初期化されてしまって違和感がでる
        window.setTimeout(async () => {
          await this.initializeMeasure()
        }, 1000)
        this.fetchData(this)
      } catch (e) {
        console.log(e)
      }
    },
    reportAction ({ url }) {
      window.open(url, '_blank')
    },
    async initializeMeasure () {
      this.measure = {
        id: null,
        name: null,
        categoryId: null,
        asin: null,
        date: null,
        beforeDate: null,
        afterDate: null,
        kpiId: null,
        beforeMemo: null,
        afterMemo: null
      }
      await this.$refs.measureFormObserver.resetForm()
    }
  }
}
</script>

<style>
</style>
